import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import {
  AIAssistConversationMessage,
  AIAssistMode,
  AIAssistRequest,
  AIAssistResponse,
  DiffChunk,
  HashLineEdit,
} from '../types';
import { ProjectFile } from '../types';
import { toHashLineContent } from './hashline';

const AI_ASSIST_TIMEOUT_MS = 540000;

const aiAssistFn = httpsCallable<AIAssistRequest, AIAssistResponse>(
  functions,
  'aiAssist',
  { timeout: AI_ASSIST_TIMEOUT_MS }
);

function isHashLineEditArray(value: unknown): value is HashLineEdit[] {
  return Array.isArray(value);
}

function isDiffChunkArray(value: unknown): value is DiffChunk[] {
  return Array.isArray(value);
}

export function normalizeAiAssistResponse(data: unknown): AIAssistResponse {
  if (!data || typeof data !== 'object') {
    throw new Error('Invalid AI response');
  }

  const summary = typeof (data as { summary?: unknown }).summary === 'string'
    ? (data as { summary: string }).summary
    : '';

  if (isHashLineEditArray((data as { edits?: unknown }).edits)) {
    return {
      summary,
      edits: (data as { edits: HashLineEdit[] }).edits,
    };
  }

  if (isDiffChunkArray((data as { chunks?: unknown }).chunks)) {
    return {
      summary,
      chunks: (data as { chunks: DiffChunk[] }).chunks,
    };
  }

  throw new Error('AI response is missing edits');
}

/**
 * Parse file references from LaTeX content
 * Extracts \input{}, \include{}, and \bibliography{} references
 */
export function parseFileReferences(content: string): string[] {
  const references: string[] = [];

  // Match \input{filename} or \input{filename.tex}
  const inputRegex = /\\input\{([^}]+)\}/g;
  let match;
  while ((match = inputRegex.exec(content)) !== null) {
    let filename = match[1];
    if (!filename.endsWith('.tex')) {
      filename += '.tex';
    }
    references.push(filename);
  }

  // Match \include{filename}
  const includeRegex = /\\include\{([^}]+)\}/g;
  while ((match = includeRegex.exec(content)) !== null) {
    let filename = match[1];
    if (!filename.endsWith('.tex')) {
      filename += '.tex';
    }
    references.push(filename);
  }

  // Match \bibliography{filename}
  const bibRegex = /\\bibliography\{([^}]+)\}/g;
  while ((match = bibRegex.exec(content)) !== null) {
    const filenames = match[1].split(',').map(f => f.trim());
    for (let filename of filenames) {
      if (!filename.endsWith('.bib')) {
        filename += '.bib';
      }
      references.push(filename);
    }
  }

  return Array.from(new Set(references)); // Remove duplicates
}

/**
 * Build smart context from active file and all project files
 */
export function buildContext(
  activeFile: ProjectFile,
  allFiles: ProjectFile[]
): { name: string; content: string }[] {
  const referencedFiles: { name: string; content: string }[] = [];
  const includedFileNames = new Set<string>();

  const addFileByName = (name: string) => {
    if (includedFileNames.has(name) || name === activeFile.name) {
      return;
    }

    const file = allFiles.find((candidate) => candidate.name === name);
    if (!file || file.type === 'folder') {
      return;
    }

    includedFileNames.add(name);
    referencedFiles.push({
      name: file.name,
      content: file.content,
    });
  };

  // Preserve the active file as the edit target, but include the root document
  // as context when working inside a subfile.
  if (activeFile.name !== 'main.tex') {
    addFileByName('main.tex');
  }

  for (const refName of parseFileReferences(activeFile.content)) {
    addFileByName(refName);
  }

  return referencedFiles;
}

/**
 * Request AI assistance
 */
export async function requestAssistance(
  projectId: string,
  prompt: string,
  activeFile: ProjectFile,
  allFiles: ProjectFile[],
  selection?: { startLine: number; endLine: number; text: string },
  conversationHistory?: AIAssistConversationMessage[],
  mode: AIAssistMode = 'edits'
): Promise<AIAssistResponse> {
  const referencedFiles = buildContext(activeFile, allFiles);

  const request: AIAssistRequest = {
    projectId,
    prompt,
    mode,
    activeFile: {
      name: activeFile.name,
      content: activeFile.content,
      hashlineContent: toHashLineContent(activeFile.content),
    },
    conversationHistory,
    referencedFiles,
    selection
  };

  const result = await aiAssistFn(request);
  return normalizeAiAssistResponse(result.data);
}

export async function requestChatAssistance(
  prompt: string,
  conversationHistory?: AIAssistConversationMessage[]
): Promise<string> {
  const request: AIAssistRequest = {
    prompt,
    mode: 'chat',
    conversationHistory,
  };

  const result = await aiAssistFn(request);
  return normalizeAiAssistResponse(result.data).summary;
}

export async function requestProjectChatAssistance(
  projectId: string,
  prompt: string,
  activeFile: ProjectFile,
  allFiles: ProjectFile[],
  conversationHistory?: AIAssistConversationMessage[]
): Promise<string> {
  const referencedFiles = buildContext(activeFile, allFiles);

  const request: AIAssistRequest = {
    projectId,
    prompt,
    mode: 'chat',
    activeFile: {
      name: activeFile.name,
      content: activeFile.content,
      hashlineContent: toHashLineContent(activeFile.content),
    },
    conversationHistory,
    referencedFiles,
  };

  const result = await aiAssistFn(request);
  return normalizeAiAssistResponse(result.data).summary;
}
